import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  addChatMedia,
  createPlaceComment,
  deleteChatMedia,
  deletePlaceComment,
  fetchChatMedia,
  fetchPlaceChatUsers,
  fetchPlaceComment,
  fetchPlaceComments,
  fetchPlaceReplyMentionComments,
  fetchFeaturedPlaceComments,
  helpfulPlaceComment,
  reportPlaceComment,
  updatePlaceComment,
  fetchPlaceChats,
} from '../endpoints/api';
import {User} from './useAuth';
import {tail} from 'lodash';
import {ToastMessage} from '../components/toast';
import {AxiosError} from 'axios';

const INTERVAL_MS = 5000; // 5 seconds

export const useInfiniteFeaturedPlaceComments = (placeId: number, enabled: boolean = true) => {
  return useInfiniteQuery(
    ['infinite_featured_place_comments', placeId],
    ({pageParam = 1}) => fetchFeaturedPlaceComments({placeId, page: pageParam}),
    {
      getNextPageParam: (lastPage) => {
        const currentPage = lastPage.data.meta?.current_page || lastPage.data.current_page;
        const lastPageNum = lastPage.data.meta?.last_page || lastPage.data.last_page;
        if (currentPage !== lastPageNum) {
          return currentPage + 1;
        }
      },
      refetchInterval: INTERVAL_MS,
      enabled: enabled && typeof placeId === 'number',
    }
  );
};

export const useInfinitePlaceComments = (placeId: number, enabled: boolean = true) => {
  return useInfiniteQuery(
    ['infinite_place_comments', placeId],
    ({pageParam = 1}) => fetchPlaceComments({placeId, page: pageParam}),
    {
      getNextPageParam: (lastPage) => {
        const currentPage = lastPage.data.meta?.current_page || lastPage.data.current_page;
        const lastPageNum = lastPage.data.meta?.last_page || lastPage.data.last_page;
        if (currentPage !== lastPageNum) {
          return currentPage + 1;
        }
      },
      refetchInterval: INTERVAL_MS,
      enabled: enabled && typeof placeId === 'number',
    }
  );
};

export const useCreatePlaceComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      placeId,
      content,
      parent_id,
      is_featured,
      user,
      hasImage = false,
    }: {
      placeId: number;
      content: string;
      parent_id?: string;
      is_featured?: boolean;
      user?: User;
      hasImage?: boolean;
    }) => createPlaceComment({placeId, content, parent_id, is_featured}),
    {
      onMutate: async (newComment) => {
        await queryClient.cancelQueries({
          queryKey: ['infinite_place_comments', newComment.placeId],
        });

        const previousComments: any = queryClient.getQueryData([
          'infinite_place_comments',
          newComment.placeId,
        ]);

        if (previousComments && previousComments.pages[0]) {
          // If this is a reply, find the parent comment to attach
          let parentComment = null;
          if (newComment.parent_id) {
            for (const page of previousComments.pages) {
              parentComment = page.data.data.find((c: any) => c.id === newComment.parent_id);
              if (parentComment) break;
            }
          }

          const newData = {
            ...previousComments,
            pages: [
              {
                ...previousComments.pages[0],
                data: {
                  ...previousComments.pages[0].data,
                  data: [
                    {
                      hasImage: true,
                      id: '1',
                      ...newComment,
                      user_id: newComment.user?.id,
                      parent: parentComment,
                    },
                    ...previousComments.pages[0].data.data,
                  ],
                },
              },
              ...tail(previousComments.pages),
            ],
          };
          queryClient.setQueryData(['infinite_place_comments', newComment.placeId], newData);
        }

        return {previousComments, newComment};
      },
      onSettled: (newComment, error, variables, context) => {
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
      },
    }
  );
};

export const useUpdatePlaceComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({placeId, commentId, content}: {placeId: number; commentId: string; content: string}) =>
      updatePlaceComment({placeId, commentId, content}),
    {
      onMutate: async (updatedComment) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: ['infinite_place_comments', updatedComment.placeId],
        });
        await queryClient.cancelQueries({
          queryKey: ['place_comments', updatedComment.placeId],
        });

        // Snapshot the previous value
        const previousComments: any = queryClient.getQueryData([
          'infinite_place_comments',
          updatedComment.placeId,
        ]);
        const previousRepliesMentions: any = queryClient.getQueryData([
          'place_comments',
          updatedComment.placeId,
        ]);

        if (previousComments && previousComments.pages) {
          // Update the comment and any children that reference it as parent
          const newData = {
            ...previousComments,
            pages: previousComments.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.map((comment: any) => {
                  // Update the comment itself
                  if (comment.id === updatedComment.commentId) {
                    return {...comment, content: updatedComment.content};
                  }
                  // Update parent reference in child comments
                  if (comment.parent && comment.parent.id === updatedComment.commentId) {
                    return {
                      ...comment,
                      parent: {...comment.parent, content: updatedComment.content},
                    };
                  }
                  return comment;
                }),
              },
            })),
          };

          queryClient.setQueryData(['infinite_place_comments', updatedComment.placeId], newData);
        }

        // Also update replies and mentions cache
        if (previousRepliesMentions?.data) {
          const updateCommentInArray = (comments: any[]) => 
            comments.map((comment: any) => {
              if (comment.id === updatedComment.commentId) {
                return {...comment, content: updatedComment.content};
              }
              if (comment.parent && comment.parent.id === updatedComment.commentId) {
                return {
                  ...comment,
                  parent: {...comment.parent, content: updatedComment.content},
                };
              }
              return comment;
            });

          const newRepliesMentions = {
            ...previousRepliesMentions,
            data: {
              ...previousRepliesMentions.data,
              replies: updateCommentInArray(previousRepliesMentions.data.replies || []),
              mentions: updateCommentInArray(previousRepliesMentions.data.mentions || []),
            },
          };

          queryClient.setQueryData(['place_comments', updatedComment.placeId], newRepliesMentions);
        }

        return {previousComments, previousRepliesMentions};
      },
      onSettled: (newComment, error, variables, context) => {
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
        queryClient.invalidateQueries(['infinite_place_comments', variables.placeId]);
        queryClient.invalidateQueries(['place_comments', variables.placeId]);
      },
    }
  );
};

export const useDeletePlaceComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({placeId, commentId}: {placeId: number; commentId: string}) =>
      deletePlaceComment({placeId, commentId}),
    {
      onMutate: async (variables) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(['infinite_place_comments', variables.placeId]);
        await queryClient.cancelQueries(['place_comments', variables.placeId]);

        // Snapshot the previous values
        const previousInfiniteComments = queryClient.getQueryData(['infinite_place_comments', variables.placeId]);
        const previousComments = queryClient.getQueryData(['place_comments', variables.placeId]);

        // Optimistically mark the comment as deleted in infinite query
        queryClient.setQueryData(['infinite_place_comments', variables.placeId], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.map((comment: any) => 
                  comment.id === variables.commentId 
                    ? {...comment, content: 'Message deleted by author', status: 0} 
                    : comment
                ),
              },
            })),
          };
        });

        // Optimistically mark the comment as deleted in regular query
        queryClient.setQueryData(['place_comments', variables.placeId], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.map((comment: any) => 
                  comment.id === variables.commentId 
                    ? {...comment, content: 'Message deleted by author', status: 0} 
                    : comment
                ),
              },
            })),
          };
        });

        return {previousInfiniteComments, previousComments};
      },
      onError: (error, variables, context: any) => {
        // Rollback on error
        if (context?.previousInfiniteComments) {
          queryClient.setQueryData(['infinite_place_comments', variables.placeId], context.previousInfiniteComments);
        }
        if (context?.previousComments) {
          queryClient.setQueryData(['place_comments', variables.placeId], context.previousComments);
        }
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
      },
      onSettled: (newComment, error, variables, context) => {
        queryClient.invalidateQueries(['infinite_place_comments', variables.placeId]);
        queryClient.invalidateQueries(['place_comments', variables.placeId]);
      },
    }
  );
};

export const useReportPlaceComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({placeId, commentId}: {placeId: number; commentId: string}) =>
      reportPlaceComment({placeId, commentId}),
    {
      onMutate: async (variables) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(['infinite_place_comments', variables.placeId]);
        await queryClient.cancelQueries(['place_comments', variables.placeId]);
        await queryClient.cancelQueries(['infinite_featured_place_comments', variables.placeId]);

        // Snapshot the previous values
        const previousInfiniteComments = queryClient.getQueryData(['infinite_place_comments', variables.placeId]);
        const previousComments = queryClient.getQueryData(['place_comments', variables.placeId]);
        const previousFeaturedComments = queryClient.getQueryData(['infinite_featured_place_comments', variables.placeId]);

        // Optimistically remove the comment from infinite query
        queryClient.setQueryData(['infinite_place_comments', variables.placeId], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.filter((comment: any) => comment.id !== variables.commentId),
              },
            })),
          };
        });

        // Optimistically remove from replies and mentions
        queryClient.setQueryData(['place_comments', variables.placeId], (old: any) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              replies: old.data.replies?.filter((comment: any) => comment.id !== variables.commentId) || [],
              mentions: old.data.mentions?.filter((comment: any) => comment.id !== variables.commentId) || [],
            },
          };
        });

        // Optimistically remove from featured comments
        queryClient.setQueryData(['infinite_featured_place_comments', variables.placeId], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.filter((comment: any) => comment.id !== variables.commentId),
              },
            })),
          };
        });

        return {previousInfiniteComments, previousComments, previousFeaturedComments};
      },
      onError: (error, variables, context: any) => {
        // Rollback on error
        if (context?.previousInfiniteComments) {
          queryClient.setQueryData(['infinite_place_comments', variables.placeId], context.previousInfiniteComments);
        }
        if (context?.previousComments) {
          queryClient.setQueryData(['place_comments', variables.placeId], context.previousComments);
        }
        if (context?.previousFeaturedComments) {
          queryClient.setQueryData(['infinite_featured_place_comments', variables.placeId], context.previousFeaturedComments);
        }
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
      },
      onSettled: (newComment, error, variables, context) => {
        queryClient.invalidateQueries(['infinite_place_comments', variables.placeId]);
        queryClient.invalidateQueries(['place_comments', variables.placeId]);
        queryClient.invalidateQueries(['infinite_featured_place_comments', variables.placeId]);
      },
    }
  );
};

export const useHelpfulPlaceComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({placeId, commentId}: {placeId: number; commentId: string}) =>
      helpfulPlaceComment({placeId, commentId}),
    {
      onSettled: (newComment, error, variables, context) => {
        queryClient.invalidateQueries(['infinite_place_comments', variables.placeId]);
        queryClient.invalidateQueries(['place_comments', variables.placeId]);
      },
    }
  );
};

export function useFetchPlaceChatUsers(placeId: number) {
  return useQuery(['place_chat_num', placeId], () => fetchPlaceChatUsers({placeId}), {
    enabled: typeof placeId === 'number',
  });
}

export function useFetchRepliesAndMentions(placeId: number, enabled: boolean = true) {
  return useQuery(['place_replies', placeId], () => fetchPlaceReplyMentionComments({placeId}), {
    enabled: enabled && typeof placeId === 'number',
    // Refetch the data every INTERVAL_MS
    refetchInterval: INTERVAL_MS,
  });
}

export function useFetchPlaceComment({placeId, commentId, enabled}) {
  return useQuery(
    ['place_comment', placeId, commentId],
    () => fetchPlaceComment({placeId, commentId}),
    {
      enabled: enabled && typeof placeId === 'number',
      // Refetch the data every INTERVAL_MS
      refetchInterval: INTERVAL_MS,
      retry: 1,
    }
  );
}

export const useAddCommentImage = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      formData,
      target_id,
      comment_id,
    }: {
      formData: FormData;
      target_id: string;
      comment_id: string;
    }) =>
      addChatMedia({
        formData,
        target_type: 'place',
        target_id,
        comment_type: 'place_comment',
        comment_id,
      }),
    {
      onSettled: (newComment, error, variables, context) => {
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
        queryClient.invalidateQueries(['infinite_place_comments', variables.target_id]);
        queryClient.invalidateQueries(['place_comments', variables.target_id]);
      },
    }
  );
};

export const useDeleteCommentImage = () => {
  const queryClient = useQueryClient();
  return useMutation(({mediaId}: {mediaId: string}) => deleteChatMedia({mediaId}), {
    onSettled: (newComment, error, variables, context) => {
      if (error && error instanceof AxiosError) {
        ToastMessage({
          status: 'error',
          text: error.message,
        });
      }
      queryClient.invalidateQueries(['infinite_place_comments']);
      queryClient.invalidateQueries(['place_comments']);
    },
  });
};

export const useFetchCommentMedia = ({mediaId}) => {
  return useQuery(['place_comment_media', mediaId], () => fetchChatMedia({mediaId}), {
    enabled: typeof mediaId === 'number',
  });
};

export const useFetchPlaceChats = () => {
  return useInfiniteQuery(
    ['infinite_place_chats'],
    ({pageParam = '1'}) => fetchPlaceChats({pageParam}),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.data.current_page !== lastPage.data.last_page) {
          return lastPage.data.current_page + 1;
        }
      },
    }
  );
};
